import React, { useEffect, useState, useCallback } from "react";
import api from "../../services/api";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  Badge,
  Button,
  Select,
  FormGroup,
  Input,
  Table,
  Thead,
  Th,
  Td,
  TRow,
  EmptyState,
  AlertBanner,
} from "../ui";

// ============================================================
// TYPES
// ============================================================

interface DailyData {
  date: number;
  total_sales: number;
  notes: string;
}

interface OCRImport {
  id: string;
  month: number;
  year: number;
  status: "processing" | "review" | "approved" | "failed";
  image_file: string;
  daily_data: DailyData[];
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const STATUS_VARIANTS: Record<string, "default" | "gold" | "blue" | "green" | "danger"> = {
  processing: "blue",
  review: "gold",
  approved: "green",
  failed: "danger",
};

// ============================================================
// COMPONENT
// ============================================================

export default function CalendarOCRPage() {
  const [imports, setImports] = useState<OCRImport[]>([]);
  const [uploadMonth, setUploadMonth] = useState(String(new Date().getMonth() + 1));
  const [uploadYear, setUploadYear] = useState(String(new Date().getFullYear()));
  const [uploadFile, setUploadFile] = useState("");
  const [uploading, setUploading] = useState(false);
  const [editingData, setEditingData] = useState<Record<string, DailyData[]>>({});

  // ---- Data fetching ----

  const fetchImports = useCallback(async () => {
    try {
      const res = await api.get("/ai/ocr-imports");
      setImports(res.data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    fetchImports();
  }, [fetchImports]);

  // ---- Actions ----

  const handleUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      await api.post("/ai/ocr-imports", {
        image_file: uploadFile,
        month: parseInt(uploadMonth, 10),
        year: parseInt(uploadYear, 10),
      });
      setUploadFile("");
      await fetchImports();
    } catch {
      /* ignore */
    } finally {
      setUploading(false);
    }
  };

  const startEditing = (imp: OCRImport) => {
    setEditingData((prev) => ({
      ...prev,
      [imp.id]: imp.daily_data.map((d) => ({ ...d })),
    }));
  };

  const updateDayValue = (importId: string, dayIndex: number, field: "total_sales" | "notes", value: string) => {
    setEditingData((prev) => {
      const data = prev[importId];
      if (!data) return prev;
      const updated = data.map((d, i) =>
        i === dayIndex
          ? { ...d, [field]: field === "total_sales" ? parseFloat(value) || 0 : value }
          : d
      );
      return { ...prev, [importId]: updated };
    });
  };

  const approveImport = async (importId: string) => {
    const data = editingData[importId];
    if (!data) return;
    try {
      await api.post(`/ai/ocr-imports/${importId}/approve`, { daily_data: data });
      setEditingData((prev) => {
        const next = { ...prev };
        delete next[importId];
        return next;
      });
      await fetchImports();
    } catch {
      /* ignore */
    }
  };

  // ---- Derived ----

  const pendingImports = imports.filter((i) => i.status === "review");
  const historyImports = imports.filter((i) => i.status !== "review");

  // ---- Render helpers ----

  const formatDate = (d: string) => {
    try {
      return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return d;
    }
  };

  const yearOptions: number[] = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear - 2; y <= currentYear + 1; y++) yearOptions.push(y);

  return (
    <PageContainer>
      <PageHeader
        title="Calendar OCR Import"
        subtitle="Import daily sales data from calendar photos"
      />

      {/* ============================================================ */}
      {/* UPLOAD SECTION */}
      {/* ============================================================ */}
      <Card className="mb-8">
        <CardHeader title="Upload Calendar Image" />

        <AlertBanner variant="blue">
          Photograph the calendar page, upload the image, and the system will extract the daily sales numbers.
          After processing, you can review and correct any values before importing.
        </AlertBanner>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <FormGroup label="Month">
            <Select value={uploadMonth} onChange={setUploadMonth}>
              {MONTHS.map((m, i) => (
                <option key={i} value={String(i + 1)}>{m}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup label="Year">
            <Select value={uploadYear} onChange={setUploadYear}>
              {yearOptions.map((y) => (
                <option key={y} value={String(y)}>{y}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup label="Image Filename">
            <Input
              value={uploadFile}
              onChange={setUploadFile}
              placeholder="e.g. calendar-may-2026.jpg"
            />
          </FormGroup>

          <div>
            <Button
              onClick={handleUpload}
              disabled={!uploadFile || uploading}
              variant="primary"
            >
              {uploading ? "Uploading..." : "Upload & Process"}
            </Button>
          </div>
        </div>
      </Card>

      {/* ============================================================ */}
      {/* PENDING REVIEW */}
      {/* ============================================================ */}
      <div className="mb-8">
        <h2 className="text-lg font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>
          Pending Review
          {pendingImports.length > 0 && (
            <span className="ml-2 text-sm font-normal" style={{ color: "var(--rbw-gray-dark)" }}>
              ({pendingImports.length})
            </span>
          )}
        </h2>

        {pendingImports.length === 0 ? (
          <EmptyState
            icon="📷"
            message="No imports awaiting review"
            detail="Upload a calendar image above to start the OCR process."
          />
        ) : (
          <div className="space-y-6">
            {pendingImports.map((imp) => {
              const isEditing = !!editingData[imp.id];
              const data = editingData[imp.id] || imp.daily_data;

              return (
                <Card key={imp.id}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold" style={{ color: "var(--rbw-navy)" }}>
                        {MONTHS[imp.month - 1]} {imp.year}
                      </span>
                      <Badge variant="gold">Review</Badge>
                      <span className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
                        {imp.image_file}
                      </span>
                    </div>
                    <div className="flex gap-2">
                      {!isEditing && (
                        <Button variant="ghost" size="sm" onClick={() => startEditing(imp)}>
                          Edit Values
                        </Button>
                      )}
                      {isEditing && (
                        <Button variant="primary" size="sm" onClick={() => approveImport(imp.id)}>
                          Approve & Import
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Editable grid */}
                  <div className="grid grid-cols-7 gap-2">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                      <div
                        key={d}
                        className="text-center text-xs font-semibold py-1"
                        style={{ color: "var(--rbw-gray-dark)" }}
                      >
                        {d}
                      </div>
                    ))}

                    {/* Calculate first day offset */}
                    {(() => {
                      const firstDay = new Date(imp.year, imp.month - 1, 1).getDay();
                      const blanks = Array.from({ length: firstDay }, (_, i) => (
                        <div key={`blank-${i}`} />
                      ));

                      const days = data.map((day, idx) => (
                        <div
                          key={day.date}
                          className="rounded-lg p-2 text-center"
                          style={{
                            background: "var(--rbw-bg)",
                            border: "1px solid var(--rbw-border)",
                            minHeight: "60px",
                          }}
                        >
                          <div className="text-xs font-medium mb-1" style={{ color: "var(--rbw-gray-dark)" }}>
                            {day.date}
                          </div>
                          {isEditing ? (
                            <input
                              type="number"
                              value={day.total_sales}
                              onChange={(e) => updateDayValue(imp.id, idx, "total_sales", e.target.value)}
                              className="w-full text-center text-sm font-semibold rounded px-1 py-0.5 outline-none"
                              style={{
                                background: "var(--rbw-bg-card)",
                                border: "1px solid var(--rbw-border)",
                                color: "var(--rbw-navy)",
                              }}
                              step="0.01"
                            />
                          ) : (
                            <div className="text-sm font-semibold" style={{ color: "var(--rbw-navy)" }}>
                              ${day.total_sales.toLocaleString()}
                            </div>
                          )}
                        </div>
                      ));

                      return [...blanks, ...days];
                    })()}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* ============================================================ */}
      {/* IMPORT HISTORY */}
      {/* ============================================================ */}
      <div>
        <h2 className="text-lg font-bold mb-4" style={{ color: "var(--rbw-navy)" }}>
          Import History
        </h2>

        {historyImports.length === 0 ? (
          <EmptyState
            icon="📁"
            message="No import history"
            detail="Completed and processed imports will appear here."
          />
        ) : (
          <Table>
            <Thead>
              <Th>Month / Year</Th>
              <Th>Image</Th>
              <Th align="center">Status</Th>
              <Th>Reviewed By</Th>
              <Th>Date</Th>
            </Thead>
            <tbody>
              {historyImports.map((imp, idx) => (
                <TRow key={imp.id} index={idx}>
                  <Td bold>
                    {MONTHS[imp.month - 1]} {imp.year}
                  </Td>
                  <Td>{imp.image_file}</Td>
                  <Td align="center">
                    <Badge variant={STATUS_VARIANTS[imp.status] || "default"}>
                      {imp.status}
                    </Badge>
                  </Td>
                  <Td>{imp.reviewed_by || "-"}</Td>
                  <Td>
                    {imp.reviewed_at ? formatDate(imp.reviewed_at) : formatDate(imp.created_at)}
                  </Td>
                </TRow>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </PageContainer>
  );
}
